import { useContext, useCallback, useMemo } from 'react'
import styled from '@emotion/styled'
import { t } from '@lingui/core/macro'
import { useQuery } from '@tanstack/react-query'
import { object } from 'prop-types'
import { CvsAutoComplete, CvsTreeSelect } from '@components'
import { connectorContext, folderContext } from '@contexts'
import { templateCopyContext } from '../context'
import { FileFormPartial } from './file-form-partial'

const FullWidth = styled.div`
  width: 100%;
`

const TreeSelect = styled(CvsTreeSelect)`
  margin-top: 24px;
  margin-left: 12px;
`

export const Box = ({ assignment }) => {
  const { onChange } = useContext(templateCopyContext)
  const { byId } = useContext(connectorContext)
  const { fetchBoxFolders } = useContext(folderContext)
  const getChildFolders = useCallback(
    id => {
      if (assignment.connector_id) {
        return fetchBoxFolders(id, assignment.connector_id).then(resp => resp && resp.data)
      }
      return null
    },
    [assignment.connector_id, fetchBoxFolders],
  )

  const { data: root } = useQuery({
    queryKey: ['boxFolders', assignment.connector_id],
    queryFn: () => getChildFolders(0),
    enabled: !!assignment.connector_id,
  })

  const connectors = useMemo(
    () =>
      Object.values(byId)
        .filter(
          connector =>
            connector.connector_type_id ===
            (assignment.connector_type_id
              ? assignment.connector_type_id
              : byId[assignment.connector_id].connector_type_id),
        )
        .map(({ name, id }) => ({
          label: name,
          value: id,
        })),
    [assignment, byId],
  )

  const onBoxChange = useCallback(
    (value, name) => {
      onChange(old => ({
        ...old.integrationsById,
        [assignment.id]: {
          ...assignment,
          [name]: value,
        },
      }))
    },
    [assignment, onChange],
  )

  const onSelected = useCallback(
    id => {
      onBoxChange(id, 'folder_name')
    },
    [onBoxChange],
  )

  const accountSelected = useMemo(() => assignment.connector_id, [assignment])
  const folderSelected = useMemo(
    () => String(assignment.folder_name ?? ''),
    [assignment.folder_name],
  )

  return (
    <FullWidth>
      <FileFormPartial assignment={assignment}>
        <CvsAutoComplete
          options={connectors}
          label={t`Box account`}
          selected={accountSelected}
          onSelected={value => onBoxChange(value, 'connector_id')}
        />
        <TreeSelect
          root={root}
          fetchChildren={getChildFolders}
          onSelected={onSelected}
          selected={folderSelected}
        />
      </FileFormPartial>
    </FullWidth>
  )
}

Box.propTypes = {
  assignment: object.isRequired,
}
